import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  Grid,
  IconButton,
} from '@mui/material';
import {Visibility as VisibilityIcon} from '@mui/icons-material';
import { formatDateToJerusalem } from '../../../utils/dateFormatter';

const OverviewTab = ({
  credential,
  showApiKey,
  setShowApiKey,
  showPassword,
  setShowPassword,
}) => {
  const formatApiKey = (apiKey) => {
    if (!apiKey) return '';
    return apiKey.length > 20
      ? `${apiKey.substring(0, 8)}...${apiKey.substring(apiKey.length - 8)}`
      : apiKey;
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  const getLivePeriodText = () => {
    if (!credential) return '';

    switch (credential.live_period_type) {
      case 'forever':
        return 'Forever';
      case 'duration':
        return `${credential.live_duration_days} days`;
      case 'until_date':
        return `Until ${new Date(
          credential.live_until_date,
        ).toLocaleDateString()}`;
      default:
        return 'Unknown';
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Basic Information */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              Basic Information
            </Typography>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <strong>Name</strong>
                  </TableCell>
                  <TableCell>{credential.credential_name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Credential Name</strong>
                  </TableCell>
                  <TableCell>
                    <Chip label={credential.credential_name} color='primary' />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={credential.is_active ? 'Active' : 'Inactive'}
                      color={getStatusColor(
                        credential.is_active ? 'active' : 'inactive',
                      )}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Live Period</strong>
                  </TableCell>
                  <TableCell>{getLivePeriodText()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>System File Upload</strong>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={credential.is_system_file_upload ? 'Yes' : 'No'}
                      color={
                        credential.is_system_file_upload ? 'primary' : 'default'
                      }
                      variant={
                        credential.is_system_file_upload ? 'filled' : 'outlined'
                      }
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Created</strong>
                  </TableCell>
                  <TableCell>
                    {formatDateToJerusalem(credential.created_at)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>

      {/* API Credentials */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              API Credentials
            </Typography>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <strong>API Key</strong>
                  </TableCell>
                  <TableCell>
                    <Box display='flex' alignItems='center' gap={1}>
                      <Typography variant='body2' fontFamily='monospace'>
                        {showApiKey
                          ? credential.api_key
                          : formatApiKey(credential.api_key)}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                {credential.username && (
                  <TableRow>
                    <TableCell>
                      <strong>Username</strong>
                    </TableCell>
                    <TableCell>{credential.username}</TableCell>
                  </TableRow>
                )}
                {credential.password && (
                  <TableRow>
                    <TableCell>
                      <strong>Password</strong>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Typography variant='body2' fontFamily='monospace'>
                          {showPassword ? credential.password : '••••••••'}
                        </Typography>
                        <IconButton
                          size='small'
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

OverviewTab.propTypes = {
  credential: PropTypes.shape({
    credential_name: PropTypes.string,
    // integration_source removed - using credential_name instead
    is_active: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    is_system_file_upload: PropTypes.bool,
    live_period_type: PropTypes.string,
    live_duration_days: PropTypes.number,
    live_until_date: PropTypes.string,
    created_at: PropTypes.string,
    api_key: PropTypes.string,
    username: PropTypes.string,
    password: PropTypes.string,
  }).isRequired,
  showApiKey: PropTypes.bool.isRequired,
  setShowApiKey: PropTypes.func.isRequired,
  showPassword: PropTypes.bool.isRequired,
  setShowPassword: PropTypes.func.isRequired,
};

export default OverviewTab;
